import { observer } from 'mobx-react-lite'
import React, { memo, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router'

//evertel
import { DisplayRoomMessage } from '@evertel/stores'
import { ContentPlaceholder, Col, Row, Text, Icon } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { RoomForwardedMessageController } from '@evertel/room'
import { ProfilePic, useUserProfile } from '@evertel/web/user'
import { parsedContent } from '../../../utils/message'
import { MessageBody } from './MessageBody'
import { MessageHeader } from './MessageHeader'
import { MessageBodyProps } from './MessageBody'


const ForwardedMessageTile: React.FC<MessageBodyProps> = observer(({
    message,
    media,
    variant = 'list-item'
}) => {

    const roomForwardedMessageController = useService(RoomForwardedMessageController, [message])

    useState(() => {
        roomForwardedMessageController.init(message)
    })

    const { openUserProfile } = useUserProfile()
    const forwardedPost = roomForwardedMessageController.message as DisplayRoomMessage

    //this isn't going to update when room is fetched because the controller dep doesn't change
    const room = roomForwardedMessageController.room
    const user = roomForwardedMessageController.user || {}
    const formattedDate = roomForwardedMessageController.formattedPublished

    const originalRoomId = roomForwardedMessageController.roomId
    const originalMessageId = forwardedPost?.id
    const originalRepliesToId = forwardedPost?.repliesToId

    const jumpToLink = useMemo(() => {
        if (!originalRoomId || !originalMessageId) return null
        const basePath = originalRepliesToId
            ? `/room/${originalRoomId}/replies/${originalRepliesToId}`
            : `/room/${originalRoomId}`
        return `${basePath}?msgId=${originalMessageId}&jump`
    }, [originalRoomId, originalMessageId, originalRepliesToId])

    const isLinkable = ['list-item', 'reply'].includes(variant) && !!jumpToLink

    const footerLabel = forwardedPost?.repliesToId ? 'Reply in #' : 'Posted in #'

    const renderFooter = () => {
        if (room?.name) {
            // only the room name gives way (ellipsis); the date always stays visible
            const footerContent = (
                <Text
                    color={isLinkable ? 'primary' : 'body-color'}
                    className="d-flex align-items-baseline min-width-0">
                    <strong
                        className="text-truncate"
                        title={`${footerLabel}${room.name}`}>
                        {footerLabel}{room.name}
                    </strong>
                    <span className="text-nowrap flex-shrink-0 ms-1">
                        | {formattedDate}
                    </span>
                </Text>
            )

            if (isLinkable) {
                return (
                    <Link
                        to={jumpToLink}
                        className="min-width-0"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        <div className="forwarded-message-link">
                            {footerContent}
                        </div>
                    </Link>
                )
            }

            return footerContent
        }

        return (
            <ContentPlaceholder
                color="gray200"
                height="26"
            >
                <Text color="body-color" className="ms-2 mt-4">{footerLabel}</Text>
            </ContentPlaceholder>
        )
    }

    return (
        <Col>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {message.isUrgent && (
                    <span className="urgent-icon">
                        <Icon
                            name="triangle-exclamation"
                        />
                    </span>
                )}

                <div
                    dangerouslySetInnerHTML={{ __html: parsedContent(message.text) }}
                    className={`post-content ${message.isUrgent ? 'urgent' : ''}`}
                    style={{ display: 'inline-block' }}
                />
            </div>
            <div className={classNames('forwarded-message-tile')}>
                <Row
                    className={classNames(
                        'message',
                        'post-' + message.id
                    )}
                >
                    <Col valign="top" className="left-col">
                        <button
                            className="p-0 pt-1 m-0"
                            tabIndex={-1}
                            aria-hidden="true"
                            onClick={() => openUserProfile(user?.id)}
                        >
                            <ProfilePic
                                userId={user?.id}
                                isGuest={false}
                            />
                        </button>
                    </Col>

                    <Col valign="top" className="center-col">
                        <MessageHeader
                            message={forwardedPost}
                            user={user}
                            isGuest={false}
                            onClickUser={openUserProfile}
                        />
                        <MessageBody
                            message={forwardedPost}
                            highlightString={''}
                            media={media}
                        />
                    </Col>
                </Row>

                <Row className="mt-4">
                    {renderFooter()}
                </Row>
            </div>
        </Col>
    )
})

ForwardedMessageTile.displayName = 'ForwardedMessageTile'

export { ForwardedMessageTile }
